// ═══════════════════════════════════════════════════════════════════════════════
// Authentication Validation Schemas
// ═══════════════════════════════════════════════════════════════════════════════
import { z } from 'zod';
import { AUTH_PROVIDER, SUPPORTED_LANGUAGES, USER_ROLE } from '../constants/index.js';
import { emailSchema, passwordSchema, nicknameSchema, usageRegionSchema } from './common.schema.js';
import { invitationCodeOptionalSchema } from './invitationCode.schema.js';
// ── Register Input Schema ──
export const registerInputSchema = z
    .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: passwordSchema,
    nickname: nicknameSchema,
    role: z.enum([USER_ROLE.EMPLOYER, USER_ROLE.HELPER], {
        error: 'Role must be employer or helper',
    }),
    /** Must be true — user completed on-screen PICS scroll + checkbox before submit */
    picsConsentAccepted: z.literal(true, {
        error: () => ({ message: 'PICS consent must be accepted' }),
    }),
    locale: z.enum(SUPPORTED_LANGUAGES, {
        error: () => ({ message: 'Locale must be a supported language' }),
    }),
    usageRegion: usageRegionSchema,
    invitationCode: invitationCodeOptionalSchema,
})
    .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});
// ── Login Input Schema ──
export const loginInputSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Password is required'),
    rememberMe: z.boolean().default(true),
});
// ── Google Auth Input Schema ──
export const googleAuthInputSchema = z.object({
    idToken: z.string().min(1, 'Google token is required'),
    mode: z.enum(['login', 'register'], {
        error: () => ({ message: 'Mode must be login or register' }),
    }),
    nickname: nicknameSchema.optional(),
    role: z
        .enum([USER_ROLE.EMPLOYER, USER_ROLE.HELPER], {
        error: 'Role must be employer or helper',
    })
        .optional(),
    locale: z
        .enum(SUPPORTED_LANGUAGES, {
        error: () => ({ message: 'Locale must be a supported language' }),
    })
        .optional(),
    usageRegion: usageRegionSchema.optional(),
    invitationCode: invitationCodeOptionalSchema,
    expectedProvider: z
        .enum([AUTH_PROVIDER.GOOGLE], {
        error: () => ({ message: 'Expected provider must be google' }),
    })
        .optional(),
});
// ── Forgot Password Input Schema ──
export const forgotPasswordInputSchema = z.object({
    email: emailSchema,
});
// ── Reset Password Input Schema ──
export const resetPasswordInputSchema = z
    .object({
    token: z.string().min(1, 'Token is required'),
    newPassword: passwordSchema,
    confirmNewPassword: passwordSchema,
})
    .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
});
// ── Change password (logged-in) ──
export const changePasswordInputSchema = z
    .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
    confirmNewPassword: passwordSchema,
})
    .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
});
// ── Verify Email Input Schema ──
export const verifyEmailInputSchema = z.object({
    token: z.string().min(1, 'Token is required'),
});
// ── Refresh Token Input Schema ──
export const refreshTokenInputSchema = z.object({
    refreshToken: z.string().min(1, 'Refresh token is required'),
});
//# sourceMappingURL=auth.schema.js.map